import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { format, formatDistanceToNow } from "date-fns";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Bot, User, Loader2, RotateCcw, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type HistoryEntry = {
  _id: string;
  entityType: "templateSection" | "globalRule";
  entityId: string;
  field: string;
  oldValue: string;
  newValue: string;
  summary: string;
  source: "user" | "agent";
  agentId?: string;
  agentName?: string;
  chatId?: string;
  createdAt: number;
};

function prettyField(field: string): string {
  switch (field) {
    case "name":
      return "Name";
    case "description":
      return "Description";
    case "examples":
      return "Examples";
    case "formatTemplate":
      return "Format template";
    case "content":
      return "Content";
    default:
      return field;
  }
}

function tryFormatExamples(value: string): string {
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) return "(no examples)";
      return parsed
        .map((e, i) => `Example ${i + 1}:\n${e}`)
        .join("\n\n");
    }
  } catch {
    // not JSON
  }
  return value;
}

function formatValue(field: string, value: string): string {
  if (field === "examples") return tryFormatExamples(value);
  return value || "(empty)";
}

type DiffOp = "equal" | "delete" | "insert";
type DiffLine = { op: DiffOp; text: string };

function diffLines(before: string, after: string): {
  beforeLines: DiffLine[];
  afterLines: DiffLine[];
} {
  const a = before.split("\n");
  const b = after.split("\n");
  const n = a.length;
  const m = b.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    new Array(m + 1).fill(0),
  );
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      if (a[i] === b[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }
  const beforeLines: DiffLine[] = [];
  const afterLines: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      beforeLines.push({ op: "equal", text: a[i] });
      afterLines.push({ op: "equal", text: b[j] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      beforeLines.push({ op: "delete", text: a[i] });
      i++;
    } else {
      afterLines.push({ op: "insert", text: b[j] });
      j++;
    }
  }
  while (i < n) {
    beforeLines.push({ op: "delete", text: a[i] });
    i++;
  }
  while (j < m) {
    afterLines.push({ op: "insert", text: b[j] });
    j++;
  }
  return { beforeLines, afterLines };
}

function DiffPane({
  lines,
  side,
  testId,
}: {
  lines: DiffLine[];
  side: "before" | "after";
  testId: string;
}) {
  const changedClass =
    side === "before"
      ? "bg-red-500/10 text-red-700 dark:text-red-300"
      : "bg-green-500/10 text-green-700 dark:text-green-300";
  return (
    <pre
      className="whitespace-pre-wrap font-mono bg-background border border-border rounded p-2 max-h-40 overflow-auto"
      data-testid={testId}
    >
      {lines.map((line, idx) => {
        const isChanged = line.op !== "equal";
        const display = line.text.length === 0 ? "\u00A0" : line.text;
        return (
          <div
            key={idx}
            className={isChanged ? `${changedClass} px-1 -mx-1 rounded-sm` : undefined}
          >
            {display}
          </div>
        );
      })}
    </pre>
  );
}

export function EditHistoryPanel({
  open,
  onOpenChange,
  userId,
  entityType,
  entityIds,
  title,
  description,
  restoreSection,
  restoreGlobalRule,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: Id<"users">;
  entityType: "templateSection" | "globalRule";
  entityIds: string[];
  title: string;
  description?: string;
  restoreSection?: {
    sectionId: Id<"templateSections">;
    currentName: string;
    currentDescription: string;
    currentExamples: string[];
    currentFormatTemplate: string;
  };
  restoreGlobalRule?: {
    ruleContents: Record<string, string>;
    onRestored?: () => void;
  };
}) {
  const { toast } = useToast();
  const restoreSectionField = useMutation(
    api.templates.restoreSectionField,
  );
  const restoreRuleContent = useMutation(
    api.globalRules.restoreRuleContent,
  );
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const result = useQuery(
    api.editHistory.listForEntities,
    open && entityIds.length > 0
      ? { userId, entityType, entityIds, limit: 50 }
      : "skip",
  );
  const entries = (result as HistoryEntry[] | undefined) || undefined;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = entries?.find((e) => e._id === selectedId) || null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[480px] sm:max-w-[480px] flex flex-col p-0"
        data-testid="sheet-edit-history"
      >
        <SheetHeader className="p-4 border-b border-border shrink-0">
          <SheetTitle>{title}</SheetTitle>
          {description && (
            <SheetDescription>{description}</SheetDescription>
          )}
        </SheetHeader>

        <div className="flex-1 flex flex-col min-h-0">
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-3 space-y-1">
              {entries === undefined ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : entries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No edits yet.
                </p>
              ) : (
                entries.map((entry) => {
                  const isSelected = selectedId === entry._id;
                  const createdAtDate = new Date(entry.createdAt);
                  const relativeTime = formatDistanceToNow(createdAtDate, {
                    addSuffix: true,
                  });
                  const absoluteTime = format(
                    createdAtDate,
                    "MMM d, yyyy 'at' h:mm a",
                  );
                  return (
                    <button
                      key={entry._id}
                      type="button"
                      onClick={() =>
                        setSelectedId(isSelected ? null : entry._id)
                      }
                      className={`w-full text-left p-3 rounded-md border transition-colors ${
                        isSelected
                          ? "border-primary/50 bg-primary/5"
                          : "border-border hover:bg-muted/50"
                      }`}
                      data-testid={`history-entry-${entry._id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground"
                                data-testid={`history-entry-time-${entry._id}`}
                              >
                                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                                {relativeTime}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {absoluteTime}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Badge
                          variant={entry.source === "agent" ? "default" : "secondary"}
                          className="text-[10px] gap-1 shrink-0"
                        >
                          {entry.source === "agent" ? (
                            <>
                              <Bot className="w-3 h-3" />
                              {entry.agentName || "Agent"}
                            </>
                          ) : (
                            <>
                              <User className="w-3 h-3" />
                              You
                            </>
                          )}
                        </Badge>
                      </div>
                      <div
                        className="text-xs text-muted-foreground mb-1"
                        data-testid={`history-entry-absolute-time-${entry._id}`}
                      >
                        {absoluteTime}
                      </div>
                      <div className="text-xs font-medium text-foreground">
                        {prettyField(entry.field)}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {entry.summary}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </ScrollArea>

          {selected && (
            <div className="border-t border-border bg-muted/20 p-3 max-h-[45%] overflow-auto shrink-0">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-semibold">
                  {prettyField(selected.field)} — diff
                </div>
                {restoreSection &&
                  entityType === "templateSection" &&
                  (selected.field === "name" ||
                    selected.field === "description" ||
                    selected.field === "examples" ||
                    selected.field === "formatTemplate") &&
                  (() => {
                    const field = selected.field as
                      | "name"
                      | "description"
                      | "examples"
                      | "formatTemplate";
                    const target = selected.oldValue ?? "";
                    let current = "";
                    if (field === "name") current = restoreSection.currentName;
                    else if (field === "description")
                      current = restoreSection.currentDescription;
                    else if (field === "examples")
                      current = JSON.stringify(
                        restoreSection.currentExamples ?? [],
                      );
                    else current = restoreSection.currentFormatTemplate;
                    let invalidReason: string | undefined;
                    if (field === "name" && !target.trim()) {
                      invalidReason = "Cannot restore to an empty name";
                    } else if (field === "examples") {
                      try {
                        const parsed = JSON.parse(target);
                        if (!Array.isArray(parsed)) {
                          invalidReason = "This history entry isn't restorable";
                        }
                      } catch {
                        invalidReason = "This history entry isn't restorable";
                      }
                    }
                    const labels: Record<typeof field, string> = {
                      name: "Name",
                      description: "Description",
                      examples: "Examples",
                      formatTemplate: "Format template",
                    };
                    const disabled =
                      restoringId === selected._id ||
                      current === target ||
                      !!invalidReason;
                    return (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        data-testid={`button-restore-${selected._id}`}
                        disabled={disabled}
                        title={invalidReason}
                        onClick={async () => {
                          setRestoringId(selected._id);
                          try {
                            await restoreSectionField({
                              sectionId: restoreSection.sectionId,
                              userId,
                              historyEntryId:
                                selected._id as Id<"editHistory">,
                              field,
                            });
                            toast({
                              title: `${labels[field]} restored`,
                              description:
                                "A new history entry was added so you can undo this too.",
                            });
                          } catch (err: unknown) {
                            const message =
                              err instanceof Error
                                ? err.message
                                : "Something went wrong restoring this version.";
                            toast({
                              title: "Couldn't restore",
                              description: message,
                              variant: "destructive",
                            });
                          } finally {
                            setRestoringId(null);
                          }
                        }}
                      >
                        {restoringId === selected._id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RotateCcw className="w-3 h-3" />
                        )}
                        Restore this version
                      </Button>
                    );
                  })()}
                {restoreGlobalRule &&
                  entityType === "globalRule" &&
                  selected.field === "content" &&
                  (() => {
                    const ruleExists = Object.prototype.hasOwnProperty.call(
                      restoreGlobalRule.ruleContents,
                      selected.entityId,
                    );
                    const current =
                      restoreGlobalRule.ruleContents[selected.entityId] ?? "";
                    const target = selected.oldValue ?? "";
                    const disabled =
                      restoringId === selected._id ||
                      !ruleExists ||
                      current === target ||
                      !target.trim();
                    return (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        data-testid={`button-restore-${selected._id}`}
                        disabled={disabled}
                        title={
                          !ruleExists
                            ? "This rule no longer exists"
                            : !target.trim()
                              ? "Cannot restore to an empty rule"
                              : undefined
                        }
                        onClick={async () => {
                          setRestoringId(selected._id);
                          try {
                            await restoreRuleContent({
                              ruleId: selected.entityId as Id<"globalRules">,
                              userId,
                              historyEntryId:
                                selected._id as Id<"editHistory">,
                            });
                            toast({
                              title: "Rule restored",
                              description:
                                "A new history entry was added so you can undo this too.",
                            });
                            restoreGlobalRule.onRestored?.();
                          } catch (err: unknown) {
                            const message =
                              err instanceof Error
                                ? err.message
                                : "Something went wrong restoring this version.";
                            toast({
                              title: "Couldn't restore",
                              description: message,
                              variant: "destructive",
                            });
                          } finally {
                            setRestoringId(null);
                          }
                        }}
                      >
                        {restoringId === selected._id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RotateCcw className="w-3 h-3" />
                        )}
                        Restore this version
                      </Button>
                    );
                  })()}
              </div>
              <div className="space-y-2 text-xs">
                {(() => {
                  const beforeText = formatValue(
                    selected.field,
                    selected.oldValue,
                  );
                  const afterText = formatValue(
                    selected.field,
                    selected.newValue,
                  );
                  const { beforeLines, afterLines } = diffLines(
                    beforeText,
                    afterText,
                  );
                  return (
                    <>
                      <div>
                        <div className="text-[10px] uppercase text-muted-foreground mb-1">
                          Before
                        </div>
                        <DiffPane
                          lines={beforeLines}
                          side="before"
                          testId="history-diff-before"
                        />
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-muted-foreground mb-1">
                          After
                        </div>
                        <DiffPane
                          lines={afterLines}
                          side="after"
                          testId="history-diff-after"
                        />
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function LastEditedLabel({
  userId,
  entityType,
  entityId,
}: {
  userId: Id<"users">;
  entityType: "templateSection" | "globalRule";
  entityId: string;
}) {
  const entries = useQuery(api.editHistory.listForEntity, {
    userId,
    entityType,
    entityId,
    limit: 1,
  }) as HistoryEntry[] | undefined;
  if (!entries || entries.length === 0) return null;
  const last = entries[0];
  const who =
    last.source === "agent" ? last.agentName || "Agent" : "You";
  return (
    <span
      className="text-[10px] text-muted-foreground"
      data-testid={`last-edited-${entityId}`}
    >
      Last edited by {who} · {format(new Date(last.createdAt), "MMM d, h:mm a")}
    </span>
  );
}
